import type { FC } from "react";

import { Box, Column, Row } from "@hightouch/htui";
import type { ChartTooltipProps } from "./types";
import {
  ChartTooltipWrapper,
  ChartTooltipTitle,
  ChartTooltipSubtitle,
  ChartTooltipValue,
  ChartTooltipValueDescription,
  ChartTooltipColor,
} from "./components";

/**
 * Tooltip to use for charts, when Hightouch UI Tooltip is not sufficient.
 * Can be used either with recharts, or as children within Hightouch UI Tooltip.
 */
export const ChartTooltip: FC<ChartTooltipProps> = ({
  wrapperVariant,
  wrapperProps = {},
  color,
  title,
  subtitles = [],
  value,
  valueDescription,
}) => {
  const tooltipContent = (
    <Row gap={2}>
      <ChartTooltipColor color={color} />
      <Column align="flex-start" gap={1} sx={{ span: { textAlign: "left" } }}>
        {title && <ChartTooltipTitle>{title}</ChartTooltipTitle>}
        {subtitles.map((subtitle, index) => (
          <ChartTooltipSubtitle key={`subtitle-${index}-${subtitle}`}>
            {subtitle}
          </ChartTooltipSubtitle>
        ))}
        {value?.map((val, index) => {
          // Generate a stable key based on the value content
          const key =
            typeof val === "string" || typeof val === "number"
              ? `value-${index}-${val}`
              : `value-${index}-${val.value}-${val.description ?? ""}`;
          return <ChartTooltipValue key={key} value={val} />;
        })}
        {valueDescription && (
          <ChartTooltipValueDescription>
            {valueDescription}
          </ChartTooltipValueDescription>
        )}
      </Column>
    </Row>
  );

  if (wrapperVariant === "full") {
    return (
      <ChartTooltipWrapper {...wrapperProps}>
        {tooltipContent}
      </ChartTooltipWrapper>
    );
  }

  if (wrapperVariant === "minimal") {
    return (
      // Need to increase spacing from standard HTUI tooltip to match ChartTooltipWrapper
      <Box py={1} px={0.5} pos="relative" {...wrapperProps}>
        {tooltipContent}
      </Box>
    );
  }

  return tooltipContent;
};
